import configs from '@/common/config';

const commonMixin = {
  data: () => ({
    attachmentModeOptions: {
      cameraAndGallery: 'cameraAndGallery',
      cameraOnly: 'cameraOnly',
      galleryOnly: 'galleryOnly',
    },
  }),
  methods: {
    mx_translateIfExist(key) {
      return this.$te(key) ? this.$t(key) : key;
    },
    mx_clone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
    /**
     * Constructs a pdf.js pdf url used in component Pdfjs.vue. Important: The url is handed over to pdf.js a query param.
     * Therefore all parameters must be separated by & instead of leading ? folowed by & for subsequent parameters.
     * @param {*} moduleBackendId
     * @param {*} file
     * @param {*} filenameAttribute
     * @returns
     */
    constructPdfJsFileUrl(moduleBackendId, file, filenameAttribute = 'filename') {
      if (!file) return null;
      return `${configs.apiUrl}/medias/${moduleBackendId}/${file[filenameAttribute]}/${file.checksum}&id=${file.id}`;
    },
    assetUrl(assetName) {
      return `${configs.apiUrl}/assets/${assetName}`;
    },
    assetUrlUnauthorized(assetName) {
      return `${configs.apiUrl}/assets-customer/${this.tenant}/${this.configs.features.backgroundImg}`;
    },
    moduleFileUrl(moduleId, filename, checksum) {
      return `${configs.apiUrl}/medias/${moduleId}/${filename}/${checksum}`;
    },
    mx_scrollTo(elementId) {
      let el = document.getElementById(elementId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
      }
    },
    mx_scrollElementIntoView(element) {
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
      }
    },

    /**
     * Helper function to return the value if defined or else the defaultValue
     * @param {*} value
     * @param {*} defaultValue
     */
    orDefault(value, defaultValue) {
      return value === undefined ? defaultValue : value;
    },
    hideKeyboard() {
      document.activeElement.blur();
    },
  },
  computed: {
    mx_instance() {
      return this.$route.params.instance;
    },
    mx_moduleTitle() {
      // console.debug('mx_moduleTitle this.$route.meta.type', this.$route.meta.type);
      if (this.$route.meta.type === 'View') {
        let key = this.$route.meta.viewConfig.title;
        return this.$te(key) ? this.$t(key) : key;
      }
      let moduleConfig = this.$store.getters['configs/getInstanceModuleConfig']({
        moduleType: this.$route.meta.moduleType,
        instance: this.$route.params.instance,
      });
      // console.debug('module config', moduleConfig);
      if (moduleConfig) {
        return this.$te(moduleConfig.title) ? this.$t(moduleConfig.title) : moduleConfig.title;
      }
      return 'moduleConfig not found for ' + this.$route.meta.moduleType + '/' + this.$route.params.instance;
    },
    mx_needsDrawer() {
      return this.$store.state.mx_needsDrawer;
    },
    locale() {
      return this.$store.state.language.locale;
    },
    mx_isLeftDrawerOpen: {
      get() {
        return this.$store.state.leftDrawer;
      },
      set(val) {
        this.$store.commit('setLeftDrawer', val);
      },
    },
    isHomeScreen() {
      return this.$route.path === '/';
    },

    isConnected() {
      return this.$store.state.isConnected;
    },
    mx_contentHeight() {
      return this.$store.state.contentHeight;
    },
    isPortraitMode() {
      return this.$store.state.orientation.type === 'portrait-primary';
    },
    isProduction() {
      return process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'prod';
    },
    isToolbarsHidden() {
      return this.$store.state.isToolbarsHidden;
    },
    toolbarsHeight() {
      let toolbarHeightMain = this.isHomeScreen ? 20 : 64;
      return this.isToolbarsHidden ? toolbarHeightMain : this.xs ? toolbarHeightMain + 96 : toolbarHeightMain + 103; //64 + 48 + 56 (sometimes -5 is necessary)
    },
    isSynchronizing() {
      return this.$store.state.sync.isSynchronizing;
    },
    theme() {
      return this.$store.state.theme.appliedTheme.components;
    },
    mx_icons() {
      return this.$store.state.theme.icons;
    },
    mx_pageBackground() {
      return this.theme.pageBackground;
    },
    darkMode() {
      return this.$vuetify.theme.dark;
    },
    themeName() {
      return this.darkMode ? 'dark' : 'light';
    },
    chipColors() {
      return `${this.theme.elements.btnTextClz} ${this.theme.elements.btnColor}`;
    },
    toolbarBg() {
      let bg = this.theme.appBar.other.bg;
      return this.darkMode ? this.orDefault(bg, 'background darken-1') : this.orDefault(bg, 'background');
    },
    /** Toolbar text and icon color */
    toolbarColor() {
      let bg = this.theme.appBar.other.color;
      return this.darkMode ? this.orDefault(bg, 'background darken-1') : this.orDefault(bg, 'background');
    },
    mx_attachmentMode() {
      const moduleConfig = this.getModuleConfig(this.$route.meta.moduleType, this.$route.params.instance);
      const defaultMode = this.attachmentModeOptions.cameraAndGallery;

      if (this.mx_isMobile || this.mx_isTablet) {
        if (this.mx_isIOs) {
          return defaultMode;
        }
        return moduleConfig && moduleConfig.attachmentMode ? moduleConfig.attachmentMode : defaultMode;
      }

      return defaultMode;
    },
    mx_showTagsInDocument() {
      const moduleConfig = this.getModuleConfig(this.$route.meta.moduleType, this.$route.params.instance);

      let showTags = true;

      if (moduleConfig && (moduleConfig.showTags !== undefined || moduleConfig.showTags !== null)) {
        showTags = moduleConfig.showTags;
      }

      return showTags;
    },
    mx_listIconColor() {
      return this.darkMode ? 'background lighten-4' : 'whitesmoke';
    },
    mx_isMobile() {
      return navigator.userAgent.includes('Mobile') || navigator.userAgent.includes('Android') || configs.browserInfo.mobile;
    },
    mx_isIPhone() {
      return navigator.userAgent.includes('iPhone');
    },
    mx_isIPad() {
      return navigator.userAgent.includes('iPad');
    },
    mx_isTablet() {
      const userAgent = navigator.userAgent.toLowerCase();
      return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent);
    },
    mx_isIOs() {
      return this.mx_isIPhone || this.mx_isIPad;
    },
    breakpoint() {
      return this.$vuetify.breakpoint;
    },
    lgAndUp() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
    mdAndUp() {
      return this.$vuetify.breakpoint.mdAndUp;
    },
    smAndUp() {
      return this.$vuetify.breakpoint.smAndUp;
    },
    mdAndDown() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    smAndDown() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    /** Convenience property to access smAndDown or mdAndUp properties in a convenient way */
    deviceRange() {
      return this.$vuetify.breakpoint.smAndDown ? 'smAndDown' : 'mdAndUp';
    },

    xs() {
      return this.$vuetify.breakpoint.xs;
    },
    sm() {
      return this.$vuetify.breakpoint.sm;
    },
    md() {
      return this.$vuetify.breakpoint.md;
    },
    lg() {
      return this.$vuetify.breakpoint.lg;
    },
    xl() {
      return this.$vuetify.breakpoint.xl;
    },
    appBarColor() {
      if (!(this.theme.appBar.home && this.theme.appBar.other)) {
        console.debug('Configuration issue. Please check ui-config.js theme.appBar in light and dark theme');
        return 'orange';
      }
      if (this.isHomeScreen) {
        let bg = this.theme.appBar.home.bg;
        return this.darkMode ? this.orDefault(bg, 'background darken-1') : this.orDefault(bg, 'background');
      }
      let bg = this.theme.appBar.other.bg;
      return this.darkMode ? this.orDefault(bg, 'background darken-1') : this.orDefault(bg, 'background');
    },
  },
};

export default commonMixin;
